import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://excerptus.io";

type SeoHeadProps = {
  title: string;
  description: string;
  /** Override path for the canonical URL. Defaults to current pathname. */
  path?: string;
  /** When true, sets <meta name="robots" content="noindex,nofollow"> and skips canonical. */
  noindex?: boolean;
};

/**
 * Sets per-route SEO metadata: <title>, meta description, OG/Twitter mirrors,
 * and a canonical link. Falls back to BASE_URL so canonicals stay stable in
 * dev/preview where window.location.origin would otherwise leak.
 *
 * Side effects only — renders nothing. The next page mounts a new <SeoHead>
 * immediately, so we only reset the robots tag on unmount (defaults to
 * index,follow) to avoid leaking a noindex into other routes.
 */
export const SeoHead = ({ title, description, path, noindex }: SeoHeadProps) => {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, attr: "name" | "property", key: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

    // Normalize to trailing-slash canonical form so /pricing and /pricing/
    // emit the same canonical URL (avoids split SEO signals). Root stays "/".
    const rawPath = path ?? location.pathname;
    const canonicalPath =
      rawPath === "/" || rawPath === ""
        ? "/"
        : rawPath.endsWith("/")
          ? rawPath
          : `${rawPath}/`;
    const canonicalUrl = canonicalPath === "/" ? BASE_URL : `${BASE_URL}${canonicalPath}`;
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);

    setMeta('meta[name="robots"]', "name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    return () => {
      // Reset robots so a noindex page doesn't leak into the next route.
      const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
      if (robots) robots.setAttribute("content", "index,follow");
    };
  }, [title, description, path, noindex, location.pathname]);

  return null;
};
